import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Customer } from '../../types/customerTypes';
import Button from '../../components/common/Button/Button';
import InputField from '../../components/common/InputField/InputField';
import { validateEmail, validatePhone } from '../../utils/validations';
import styles from './CustomerForm.module.scss';

interface CustomerFormProps {
  customer?: Customer;
  onSubmit: (customerData: Partial<Customer>) => Promise<void>;
  loading?: boolean;
  mode: 'create' | 'edit';
}

const CustomerForm: React.FC<CustomerFormProps> = ({
  customer,
  onSubmit,
  loading = false,
  mode
}) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zip_code: '',
    country: '',
    is_active: true,
    is_old_url: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Initialize form with customer data if editing
  useEffect(() => {
    if (customer && mode === 'edit') {
      setFormData({
        name: customer.name,
        email: customer.email,
        phone: customer.phone,
        address: customer.address,
        city: customer.city,
        state: customer.state,
        zip_code: customer.zip_code,
        country: customer.country,
        is_active: customer.is_active,
        is_old_url: customer.is_old_url || '',
      });
    }
  }, [customer, mode]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    // Required fields
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!validateEmail(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!validatePhone(formData.phone)) {
      newErrors.phone = 'Please enter a valid phone number';
    }

    if (!formData.address.trim()) {
      newErrors.address = 'Address is required';
    }

    if (!formData.city.trim()) {
      newErrors.city = 'City is required';
    }

    if (!formData.state.trim()) {
      newErrors.state = 'State is required';
    }

    if (!formData.zip_code.trim()) {
      newErrors.zipCode = 'ZIP code is required';
    }

    if (!formData.country.trim()) {
      newErrors.country = 'Country is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      await onSubmit(formData);
    } catch (error) {
      console.error('Failed to save customer:', error);
    }
  };

  const handleCancel = () => {
    navigate('/customers');
  };

  return (
    <div className={styles.customerForm}>
      <div className={styles.header}>
        <h2>{mode === 'create' ? 'Add New Customer' : 'Edit Customer'}</h2>
        <p>
          {mode === 'create' 
            ? 'Create a new customer record with their contact information.'
            : 'Update customer information and contact details.'
          }
        </p>
      </div>

      <form onSubmit={handleSubmit} className={styles.form}>
        <div className={styles.formGrid}>
          {/* Basic Information */}
          <div className={styles.section}>
            <h3>Basic Information</h3>
            <div className={styles.fieldGroup}>
              <InputField
                label="Full Name"
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder="Enter customer's full name"
                error={errors.name}
                required
              />
            </div>
            <div className={styles.fieldGroup}>
              <InputField
                label="Email Address"
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="Enter email address"
                error={errors.email}
                required
              />
            </div>
            <div className={styles.fieldGroup}>
              <InputField
                label="Phone Number"
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                placeholder="Enter phone number"
                error={errors.phone}
                required
              />
            </div>
          </div>

          {/* Address Information */}
          <div className={styles.section}>
            <h3>Address Information</h3>
            <div className={styles.fieldGroup}>
              <InputField
                label="Street Address"
                type="text"
                name="address"
                value={formData.address}
                onChange={handleInputChange}
                placeholder="Enter street address"
                error={errors.address}
                required
              />
            </div>
            <div className={styles.fieldRow}>
              <div className={styles.fieldGroup}>
                <InputField
                  label="City"
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleInputChange}
                  placeholder="Enter city"
                  error={errors.city}
                  required
                />
              </div>
              <div className={styles.fieldGroup}>
                <InputField
                  label="State/Province"
                  type="text"
                  name="state"
                  value={formData.state}
                  onChange={handleInputChange}
                  placeholder="Enter state"
                  error={errors.state}
                  required
                />
              </div>
            </div>
            <div className={styles.fieldRow}>
              <div className={styles.fieldGroup}>
                <InputField
                  label="ZIP/Postal Code"
                  type="text"
                  name="zipCode"
                  value={formData.zip_code}
                  onChange={handleInputChange}
                  placeholder="Enter ZIP code"
                  error={errors.zipCode}
                  required
                />
              </div>
              <div className={styles.fieldGroup}>
                <InputField
                  label="Country"
                  type="text"
                  name="country"
                  value={formData.country}
                  onChange={handleInputChange}
                  placeholder="Enter country"
                  error={errors.country}
                  required
                />
              </div>
            </div>
          </div>
        </div>

        {/* Status */}
        <div className={styles.section}>
          <h3>Status</h3>
          <div className={styles.statusField}>
            <label className={styles.checkboxLabel}>
              <input
                type="checkbox"
                name="is_active"
                checked={formData.is_active}
                onChange={handleInputChange}
                className={styles.checkbox}
              />
              <span className={styles.checkboxText}>
                Customer is active and can receive invoices
              </span>
            </label>
          </div>
        </div>

        {/* Legacy Customer */}
        <div className={styles.section}>
          <h3>Legacy Customer</h3>
          <div className={styles.fieldGroup}>
            <InputField
              label="PDF URL"
              type="text"
              name="is_old_url"
              value={formData.is_old_url}
              onChange={handleInputChange}
              placeholder="Enter balance URL"
              error={errors.is_old_url}
            />
          </div>
        </div>

        {/* Form Actions */}
        <div className={styles.actions}>
          <Button
            type="button"
            variant="secondary"
            onClick={handleCancel}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            loading={loading}
          >
            {mode === 'create' ? 'Create Customer' : 'Update Customer'}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default CustomerForm; 